import React from 'react';

export default function FlightSummaryCard({
  aircraft,
  activeBooking,
  colour,
  flightStatus,
  dateLabel,
  endDateLabel,
  startTime,
  endTime,
  isMultiDay,
}) {
  return (
    <>
      <div style={styles.kicker}>
        FLIGHT CENTRE
      </div>

      <div style={styles.aircraftHero}>
        <div style={styles.aircraftRegRow}>
          <span
            style={{
              ...styles.aircraftDot,
              background: colour,
            }}
          />

          <span>
            {getAircraftRegistration(
              aircraft,
              activeBooking
            )}
          </span>
        </div>

        <div style={styles.aircraftType}>
          {aircraft?.icao_type ||
            aircraft?.type ||
            'Aircraft'}
        </div>

        <div
          style={getAircraftStatusStyle(
            aircraft?.status
          )}
        >
          {getAircraftStatusLabel(
            aircraft?.status
          )}
        </div>
      </div>

      <div style={styles.flightStatus}>
        {flightStatus}
      </div>

      {isMultiDay ? (
        <div style={styles.multiDayPanel}>
          <div style={styles.journeyPoint}>
            <div style={styles.journeyLabel}>
              Outbound
            </div>

            <div style={styles.journeyDate}>
              {dateLabel}
            </div>

            <div style={styles.journeyTime}>
              {startTime}
            </div>
          </div>

          <div style={styles.journeyDivider}>
            →
          </div>

          <div style={styles.journeyPoint}>
            <div style={styles.journeyLabel}>
              Return
            </div>

            <div style={styles.journeyDate}>
              {endDateLabel}
            </div>

            <div style={styles.journeyTime}>
              {endTime}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div style={styles.date}>
            {dateLabel}
          </div>

          <div style={styles.timeBand}>
            {startTime}

            <span style={styles.timeArrow}>
              →
            </span>

            {endTime}
          </div>
        </>
      )}
    </>
  );
}

export function getAircraftRegistration(
  aircraft,
  activeBooking
) {
  return String(
    aircraft?.registration ||
      activeBooking?.aircraftRegistration ||
      activeBooking?.registration ||
      'Aircraft'
  ).trim() || 'Aircraft';
}

function getAircraftStatusLabel(status) {
  const value = String(
    status || 'serviceable'
  ).toLowerCase();

  if (value === 'aog') return '🔴 AOG';

  if (value === 'defect') {
    return '🔴 DEFECT';
  }

  if (value === 'deferred') {
    return '🟠 DEFERRED';
  }

  return '🟢 SERVICEABLE';
}

function getAircraftStatusStyle(status) {
  const value = String(
    status || 'serviceable'
  ).toLowerCase();

  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '0.65rem',
    padding: '0.24rem 0.65rem',
    borderRadius: 999,
    fontSize: '0.66rem',
    fontWeight: 950,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
  };

  if (
    value === 'aog' ||
    value === 'defect'
  ) {
    return {
      ...base,
      color: '#ffb3b3',
      background:
        'rgba(255, 77, 77, 0.14)',
      border:
        '1px solid rgba(255, 77, 77, 0.28)',
    };
  }

  if (value === 'deferred') {
    return {
      ...base,
      color: '#ffd27a',
      background:
        'rgba(255, 210, 122, 0.12)',
      border:
        '1px solid rgba(255, 210, 122, 0.26)',
    };
  }

  return {
    ...base,
    color: '#8ff4b6',
    background:
      'rgba(143, 244, 182, 0.11)',
    border:
      '1px solid rgba(143, 244, 182, 0.24)',
  };
}

const styles = {
  kicker: {
    color: '#55c7ff',
    fontSize: '0.72rem',
    fontWeight: 950,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    textAlign: 'center',
    marginBottom: '0.8rem',
  },

  aircraftHero: {
    textAlign: 'center',
    padding: '0.95rem',
    borderRadius: 18,
    background:
      'linear-gradient(180deg, rgba(34,42,63,0.94), rgba(20,27,43,0.98))',
    border:
      '1px solid rgba(120,160,210,0.15)',
    boxShadow:
      '0 12px 30px rgba(0,0,0,0.22)',
  },

  aircraftRegRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.45rem',
    color: '#ffffff',
    fontSize: '1.55rem',
    fontWeight: 950,
    letterSpacing: '0.03em',
  },

  aircraftDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    boxShadow:
      '0 0 8px rgba(255,255,255,0.22)',
  },

  aircraftType: {
    marginTop: '0.25rem',
    color: '#8db7e6',
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
  },

  flightStatus: {
    marginTop: '0.85rem',
    textAlign: 'center',
    color: '#ffffff',
    fontSize: '0.72rem',
    fontWeight: 950,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
  },

  date: {
    marginTop: '0.8rem',
    textAlign: 'center',
    color: '#ffffff',
    fontSize: '1rem',
    fontWeight: 850,
  },

  timeBand: {
    marginTop: '0.45rem',
    textAlign: 'center',
    color: '#55c7ff',
    fontSize: '1.7rem',
    fontWeight: 950,
    letterSpacing: '0.03em',
  },

  timeArrow: {
    color: '#8db7e6',
    margin: '0 0.5rem',
  },

  multiDayPanel: {
    display: 'grid',
    gridTemplateColumns:
      'minmax(0, 1fr) auto minmax(0, 1fr)',
    gap: '0.75rem',
    alignItems: 'center',
    marginTop: '0.8rem',
    padding: '0.8rem',
    borderRadius: 14,
    background:
      'rgba(255,255,255,0.045)',
    border:
      '1px solid rgba(255,255,255,0.08)',
  },

  journeyPoint: {
    minWidth: 0,
    textAlign: 'center',
  },

  journeyLabel: {
    color: '#8db7e6',
    fontSize: '0.66rem',
    fontWeight: 950,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  },

  journeyDate: {
    marginTop: '0.25rem',
    color: '#ffffff',
    fontSize: '0.78rem',
    fontWeight: 750,
    lineHeight: 1.3,
  },

  journeyTime: {
    marginTop: '0.25rem',
    color: '#55c7ff',
    fontSize: '1.25rem',
    fontWeight: 950,
  },

  journeyDivider: {
    color: '#8db7e6',
    fontSize: '1.25rem',
    fontWeight: 900,
  },
};